import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppSelector, useAppDispatch } from '../hooks'
import { addRichNotification } from '../stores/RichNotificationStore'
import Parse from '../services/initializeParse'
import { isOrganiserOrActiveInvitee, readMyUpcomingBookings } from '../services/myBookingsRead'
import { parseUserId } from '../utils/parseUserId'
import type { RoomBookingData } from '../../../types/RoomBooking'
import {
  claimMeetingReminder,
  hasShownMeetingReminder,
  startsInText,
} from '../utils/meetingReminderShown'

/**
 * How often the dashboard re-reads its meetings. Booking changes are only pushed to players in a
 * game room, so without this a meeting someone creates while the user sits on the dashboard
 * would get no reminder until a reload.
 */
export const DASHBOARD_REMINDER_REFRESH_MS = 5 * 60_000

/**
 * Least time between a read and one made because the tab became visible again. Each read loads
 * the company's upcoming bookings, so quick tab switching must not turn into a read per switch.
 */
export const DASHBOARD_REMINDER_TAB_RETURN_GAP_MS = 60_000

/**
 * Longest wait between timed or tab-return reads while they keep failing (SC-477): each failure
 * in a row doubles the 5 minutes, up to this. A booking change still reads at once.
 */
export const DASHBOARD_REMINDER_MAX_BACKOFF_MS = 20 * 60_000

const tzAbbr =
  new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
    .formatToParts(new Date())
    .find((p) => p.type === 'timeZoneName')?.value || ''

/** Cloud stores 15 when a booking predates the setting. */
const remindBeforeOf = (b: RoomBookingData) => b.remindBefore ?? 15

const reminderRef = (b: RoomBookingData) => ({
  bookingId: b.objectId!,
  startTime: b.startTime,
  remindBefore: remindBeforeOf(b),
})

function formatTime(iso: string): string {
  return (
    new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) +
    (tzAbbr ? ` ${tzAbbr}` : '')
  )
}

/**
 * Schedules client-side meeting reminder notifications on the dashboard, firing a rich
 * notification at `startTime - remindBefore` for each upcoming meeting.
 *
 * It reads its own list (upcoming meetings the user created or is invited to, declined ones left
 * out by cloud) rather than the Meetings page's, so the reminder works from wherever the user
 * lands, and that page's Today/Past filter cannot drop a pending reminder. The list is read on
 * landing, after every booking change, every `DASHBOARD_REMINDER_REFRESH_MS` and when the tab
 * becomes visible again (unless the last read is under `DASHBOARD_REMINDER_TAB_RETURN_GAP_MS` old).
 * After failed reads, the timed and tab-return reads wait 10, then 20 minutes.
 *
 * Skips a meeting already reminded about in the game room (shared record in
 * `meetingReminderShown`), and one that has already started: a late "starts in" reminder for a
 * running meeting is wrong, and the meetings list already shows it as live.
 *
 * @param companyId The company whose meetings to read, or null while there is none or its access
 *   is not open (a suspended company's meetings are refused by cloud anyway).
 */
export function useDashboardBookingReminders(companyId: string | null) {
  const dispatch = useAppDispatch()
  const bookingsRefreshTrigger = useAppSelector((s) => s.roomBooking.bookingsRefreshTrigger)
  const [bookings, setBookings] = useState<RoomBookingData[]>([])
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  // Only the latest read may land, so a slow earlier answer never replaces a newer list.
  const readSeqRef = useRef(0)
  const lastReadAtRef = useRef(0)
  const failedReadsRef = useRef(0)
  // Read at call time so the timed and tab-return reads see the latest trigger without
  // re-creating `readBookings` (and re-running the landing read) on every booking change.
  const triggerRef = useRef(bookingsRefreshTrigger)
  triggerRef.current = bookingsRefreshTrigger

  const readBookings = useCallback(async () => {
    const seq = ++readSeqRef.current
    if (!companyId) {
      setBookings([])
      return
    }
    lastReadAtRef.current = Date.now()
    try {
      // Shared with the dashboard's meetings list, which asks for the same thing at the same time.
      const list = await readMyUpcomingBookings(companyId, triggerRef.current)
      failedReadsRef.current = 0
      // Defence in depth: cloud already limits the list to the user's own meetings, but a
      // reminder for someone else's is worse than a missed one, so a booking that is not the
      // user's (or an unknown user) is left out here too.
      const userId = parseUserId(Parse)
      const mine = userId ? list.filter((b) => isOrganiserOrActiveInvitee(b, userId)) : []
      if (seq === readSeqRef.current) setBookings(mine)
    } catch (err) {
      // Keep the last list: its reminders are still right, and the next read may succeed.
      failedReadsRef.current += 1
      console.warn('[useDashboardBookingReminders] fetch failed:', err)
    }
  }, [companyId])

  useEffect(() => {
    void readBookings()
  }, [readBookings, bookingsRefreshTrigger])

  useEffect(() => {
    if (!companyId) return
    const sinceLastRead = () => Date.now() - lastReadAtRef.current
    const backoffMs = () =>
      failedReadsRef.current === 0
        ? 0
        : Math.min(DASHBOARD_REMINDER_REFRESH_MS * 2 ** failedReadsRef.current, DASHBOARD_REMINDER_MAX_BACKOFF_MS)
    const refreshIfVisible = () => {
      if (document.visibilityState === 'hidden' || sinceLastRead() < backoffMs()) return
      void readBookings()
    }
    const refreshOnTabReturn = () => {
      if (sinceLastRead() < DASHBOARD_REMINDER_TAB_RETURN_GAP_MS) return
      refreshIfVisible()
    }
    const interval = setInterval(refreshIfVisible, DASHBOARD_REMINDER_REFRESH_MS)
    document.addEventListener('visibilitychange', refreshOnTabReturn)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', refreshOnTabReturn)
    }
  }, [companyId, readBookings])

  // Invalidates any read still in flight when the dashboard unmounts.
  useEffect(() => () => {
    readSeqRef.current += 1
  }, [])

  useEffect(() => {
    const timers = timersRef.current
    const now = Date.now()

    for (const b of bookings) {
      if (!b.objectId || b.status === 'CANCELLED') continue
      // "No reminder" opts out of the in-app reminder too, not just the email.
      if (b.remindBefore === 0) continue
      if (hasShownMeetingReminder(reminderRef(b))) continue

      const startMs = new Date(b.startTime).getTime()
      if (!(now < startMs)) continue
      const reminderMs = startMs - remindBeforeOf(b) * 60_000
      const delayMs = reminderMs - now

      if (delayMs <= 0) {
        // Already inside the reminder window and not started yet: fire now
        fireReminder(b)
      } else if (!timers.has(b.objectId)) {
        timers.set(
          b.objectId,
          setTimeout(() => {
            timers.delete(b.objectId!)
            fireReminder(b)
          }, delayMs)
        )
      }
    }

    return () => {
      for (const t of timers.values()) clearTimeout(t)
      timers.clear()
    }
  }, [bookings, dispatch])

  function fireReminder(b: RoomBookingData) {
    const minutesLeft = (new Date(b.startTime).getTime() - Date.now()) / 60_000
    // A throttled background-tab timer can run after the start
    if (!(minutesLeft > 0)) return
    if (!claimMeetingReminder(reminderRef(b))) return
    dispatch(
      addRichNotification({
        title: 'Meeting Reminder',
        lines: [
          { text: b.title, bold: true },
          ...(b.roomName ? [{ text: b.roomName, secondary: true }] : []),
          {
            text: `${startsInText(minutesLeft)} (${formatTime(b.startTime)})`,
            secondary: true,
          },
        ],
      })
    )
  }
}
